import axios from 'axios';
import { getAuthHeader } from './authService';

const API_URL = process.env.REACT_APP_API_BASE_URL || '/api';

export interface Assessment {
  id: number;
  title: string;
  description: string;
  category: string;
  instructions: string;
  estimatedMinutes: number;
  isActive: boolean;
  redFlagThreshold: number;
  yellowFlagThreshold: number;
  greenFlagThreshold: number;
  totalQuestions: number;
  createdAt: string;
  updatedAt: string;
}

export interface AssessmentDetail extends Assessment {
  questions: Question[];
}

export interface Question {
  id: number;
  questionText: string;
  questionType: string;
  isRequired: boolean;
  orderIndex: number;
  pointsIfApplicable: number;
  options: Option[];
}

export interface Option {
  id: number;
  optionText: string;
  pointValue: number;
  orderIndex: number;
  flagIfSelected: boolean;
}

export interface AssessmentSubmission {
  studentId: number;
  responses: ResponseSubmission[];
  completionTimeMinutes: number;
}

export interface ResponseSubmission {
  questionId: number;
  selectedOptionIds: number[];
  responseText: string;
  pointValue: number;
}

export interface AssessmentResult {
  submissionId: number;
  totalScore: number;
  riskLevel: string;
  hasImmediateFlags: boolean;
  requiresFollowUp: boolean;
  message: string;
  submittedAt: string;
}

export interface StudentAssignment {
  assignmentId: number;
  assessmentId: number;
  assessmentTitle: string;
  completionStatus: string;
  assignedAt: string;
  dueDate: string;
  completedAt: string | null;
  assignedByName: string;
  notes: string;
}

export interface AssessmentStatistics {
  assessmentId: number;
  assessmentTitle: string;
  totalAssignments: number;
  completedAssignments: number;
  pendingAssignments: number;
  completionRate: number;
  averageScore: number;
  redFlagCount: number;
  yellowFlagCount: number;
  greenFlagCount: number;
}

class AssessmentService {
  async getAssessments(page = 0, size = 10, search?: string, category?: string, active?: boolean) {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });
    
    if (search) params.append('search', search);
    if (category) params.append('category', category);
    if (active !== undefined) params.append('active', active.toString());

    const response = await axios.get(`${API_URL}/assessments?${params}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  }

  async getAssessmentById(id: number): Promise<AssessmentDetail> {
    const response = await axios.get(`${API_URL}/assessments/${id}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  }

  async createAssessment(assessment: Partial<Assessment>) {
    const response = await axios.post(`${API_URL}/assessments`, assessment, {
      headers: getAuthHeader(),
    });
    return response.data;
  }

  async updateAssessment(id: number, assessment: Partial<Assessment>) {
    const response = await axios.put(`${API_URL}/assessments/${id}`, assessment, {
      headers: getAuthHeader(),
    });
    return response.data;
  }

  async deleteAssessment(id: number) {
    await axios.delete(`${API_URL}/assessments/${id}`, {
      headers: getAuthHeader(),
    });
  }

  async assignAssessment(assessmentId: number, studentIds: number[], dueDate?: string, notes?: string, sendNotification = true) {
    const response = await axios.post(`${API_URL}/assessments/${assessmentId}/assign`, {
      studentIds,
      dueDate,
      notes,
      sendNotification,
    }, {
      headers: getAuthHeader(),
    });
    return response.data;
  }

  async getAssignedAssessments(studentId: number, page = 0, size = 10, status?: string): Promise<StudentAssignment[]> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });
    
    if (status) params.append('status', status);

    const response = await axios.get(`${API_URL}/assessments/student/${studentId}/assigned?${params}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  }

  async submitAssessment(assessmentId: number, submission: AssessmentSubmission): Promise<AssessmentResult> {
    const response = await axios.post(`${API_URL}/assessments/${assessmentId}/submit`, submission, {
      headers: getAuthHeader(),
    });
    return response.data;
  }

  async getStudentResponses(assessmentId: number, studentId: number) {
    const response = await axios.get(`${API_URL}/assessments/${assessmentId}/responses/${studentId}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  }

  async updateResponse(responseId: number, responseData: Partial<ResponseSubmission>) {
    const response = await axios.put(`${API_URL}/assessments/responses/${responseId}`, responseData, {
      headers: getAuthHeader(),
    });
    return response.data;
  }

  async getAssessmentCategories(): Promise<string[]> {
    const response = await axios.get(`${API_URL}/assessments/categories`, {
      headers: getAuthHeader(),
    });
    return response.data;
  }

  async duplicateAssessment(id: number, newTitle?: string) {
    const response = await axios.post(`${API_URL}/assessments/${id}/duplicate`, { newTitle }, {
      headers: getAuthHeader(),
    });
    return response.data;
  }

  async getAssessmentStatistics(id: number): Promise<AssessmentStatistics> {
    const response = await axios.get(`${API_URL}/assessments/${id}/statistics`, {
      headers: getAuthHeader(),
    });
    return response.data;
  }

  async activateAssessment(id: number) {
    await axios.post(`${API_URL}/assessments/${id}/activate`, {}, {
      headers: getAuthHeader(),
    });
  }

  async deactivateAssessment(id: number) {
    await axios.post(`${API_URL}/assessments/${id}/deactivate`, {}, {
      headers: getAuthHeader(),
    });
  }

  // PDF Report Methods
  async downloadAssessmentPdf(studentId: number, assessmentId: number) {
    try {
      const response = await axios.get(
        `${API_URL}/reports/pdf/student/${studentId}/assessment/${assessmentId}`, 
        {
          headers: getAuthHeader(),
          responseType: 'blob',
        }
      );
      
      // Check if the response is an error (content-type would be application/json for errors)
      const contentType = response.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        // Convert blob to text to read error message
        const text = await response.data.text();
        const error = JSON.parse(text);
        throw new Error(error.error || 'Failed to download PDF');
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `assessment_report_${studentId}_${assessmentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      // Check if it's a blob error response
      if (error.response && error.response.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.error || 'Failed to download PDF');
        } catch {
          throw new Error('Failed to download PDF');
        }
      }
      throw error;
    }
  }

  async downloadComprehensivePdf(studentId: number) {
    const response = await axios.get(
      `${API_URL}/reports/pdf/student/${studentId}/comprehensive`, 
      {
        headers: getAuthHeader(),
        responseType: 'blob',
      }
    );
    
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `comprehensive_report_${studentId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  async downloadProviderSummaryPdf(providerId: number, startDate?: Date, endDate?: Date) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate.toISOString());
    if (endDate) params.append('endDate', endDate.toISOString());

    const response = await axios.get(
      `${API_URL}/reports/pdf/provider/${providerId}/summary?${params}`, 
      {
        headers: getAuthHeader(),
        responseType: 'blob',
      }
    );
    
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `provider_summary_${providerId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}

export default new AssessmentService();